import { ReactNode, useMemo } from "react"

import type { ILock } from "@/domains/lock"
import { BodyLg } from "@/domains/design/components/body-lg"
import { BodySM } from "@/domains/design/components/body-sm"
import { BodyXs } from "@/domains/design/components/body-xs"
import { cn, formatCurrency, formatPercentage } from "@/domains/library"
import useResponsive from "@/hooks/use-responsive"
import { useVestedToken } from "@/hooks/use-vested-token"

import { DisplayLevel } from "../../../../domains/common/components/display-level"

type UserLockStats = {
  nfts: ILock[]
  veTotalSupply: number
}

export const UserLockStats = ({ nfts, veTotalSupply }: UserLockStats) => {
  const vestedToken = useVestedToken()
  const { isMobile } = useResponsive()

  const largestNftValue = useMemo(() => {
    const size = Math.max(...nfts.map((nft) => Number(nft.lockValue)))
    if (size === -Infinity) {
      return 0
    }
    return size
  }, [nfts])

  const userLocked = useMemo(() => {
    const locked = nfts.reduce((acc, nft) => acc + Number(nft.lockAmount), 0)
    return locked
  }, [nfts])

  const userVePower = useMemo(() => {
    const power = nfts.reduce((acc, nft) => acc + Number(nft.lockValue), 0)
    return power
  }, [nfts])

  const userPower = userVePower / userLocked

  const userInfluence = Number(userLocked) / Number(veTotalSupply)

  if (isMobile) {
    return (
      <div className="mb-8 grid grid-cols-6 gap-2 gap-y-4 rounded-lg border border-p400-light bg-p400 px-2 py-3">
        <MobileCard title="Locked" value={formatCurrency(userLocked, 4)} className="col-span-3" />
        <MobileCard title={vestedToken.symbol} value={formatCurrency(userVePower, 4)} className="col-span-3" />
        <MobileCard title="Level" value={<DisplayLevel lockValue={largestNftValue} />} className="col-span-2" />
        <MobileCard title="Influence" value={formatPercentage(userInfluence, 4)} className="col-span-2" />
        <MobileCard title="Power" value={formatPercentage(userPower, 2)} className="col-span-2" />
      </div>
    )
  }
  return (
    <div className="mb-8 grid grid-cols-5 justify-center rounded-lg border border-p400-light bg-p400 p-4">
      <DesktopCard
        title="Level"
        value={
          <div className="">
            <DisplayLevel lockValue={largestNftValue} />
          </div>
        }
        className=""
      />
      <DesktopCard title="Influence" value={formatPercentage(userInfluence, 4)} className="" />
      <DesktopCard title="Power" value={formatPercentage(userPower, 2)} className="" />
      <DesktopCard title="Locked" value={formatCurrency(userLocked, 4)} className="" />
      <DesktopCard title={vestedToken.symbol} value={formatCurrency(userVePower, 4)} className="" />
    </div>
  )
}

const DesktopCard = ({ title, value, className }: { title?: string; value: ReactNode; className?: string }) => {
  if (!title) {
    return null
  }
  return (
    <div className={cn("grid items-center justify-center gap-0.5 align-sub", className)}>
      <BodyXs className="text-center text-t300">{title}</BodyXs>
      {typeof value === "string" ? <BodyLg className="text-center text-t100">{value}</BodyLg> : value}
    </div>
  )
}

const MobileCard = ({ title, value, className }: { title?: string; value: ReactNode; className?: string }) => {
  if (!title) {
    return null
  }

  return (
    <div className={cn("grid items-center justify-center gap-0.5 align-sub", className)}>
      <BodyXs className="text-center text-t300">{title}</BodyXs>
      <BodyLg className="flex justify-center text-t100">{value}</BodyLg>
    </div>
  )
}
